/**
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/tabs/}
 */
import { getAriaControlsElement, setAriaSelected } from "./base";

export function isTab(element: HTMLElement): element is HTMLButtonElement {
  return element instanceof HTMLButtonElement && element.role === "tab";
}

export function handleTabClick(tab: HTMLButtonElement) {
  const tabToSelect = tab;
  const tabs = getTabs(tab);
  for (const tab of tabs) {
    if (tab === tabToSelect) {
      setAriaSelected(tab, true);
      tab.removeAttribute("tabindex");

      const tabpanel = getAriaControlsElement(tab);
      if (tabpanel) {
        tabpanel.removeAttribute("hidden");
      }
    } else {
      setAriaSelected(tab, false);
      tab.tabIndex = -1;

      const tabpanel = getAriaControlsElement(tab);
      if (tabpanel) {
        tabpanel.hidden = true;
      }
    }
  }
}

export function handleTabKeyDown(tab: HTMLButtonElement, key: string): boolean {
  const tablist = tab.closest('[role="tablist"]');
  if (!tablist) {
    return false;
  }

  const orientation = tablist.getAttribute("aria-orientation");
  if (typeof orientation === "string") {
    if (orientation === "vertical") {
      return handleVerticalTabKeyDown(tab, key);
    } else {
      return handleHorizontalTabKeyDown(tab, key);
    }
  } else {
    return handleHorizontalTabKeyDown(tab, key);
  }
}

function handleHorizontalTabKeyDown(tab: HTMLButtonElement, key: string) {
  switch (key) {
    case "ArrowLeft":
      selectPreviousTab(tab);
      return true;
    case "ArrowRight":
      selectNextTab(tab);
      return true;
    case "Home":
      selectFirstTab(tab);
      return true;
    case "End":
      selectLastTab(tab);
      return true;
    default:
      return false;
  }
}

function handleVerticalTabKeyDown(tab: HTMLButtonElement, key: string) {
  switch (key) {
    case "ArrowUp":
      selectPreviousTab(tab);
      return true;
    case "ArrowDown":
      selectNextTab(tab);
      return true;
    case "Home":
      selectFirstTab(tab);
      return true;
    case "End":
      selectLastTab(tab);
      return true;
    default:
      return false;
  }
}

function selectPreviousTab(tab: HTMLButtonElement) {
  const tabs = getTabs(tab);
  const index = tabs.indexOf(tab);
  if (index !== -1 && index > 0) {
    tabs[index - 1].focus();
  }
}

function selectNextTab(tab: HTMLButtonElement) {
  const tabs = getTabs(tab);
  const index = tabs.indexOf(tab);
  if (index !== -1 && index < tabs.length - 1) {
    tabs[index + 1].focus();
  }
}

function selectFirstTab(tab: HTMLButtonElement) {
  const tabs = getTabs(tab);
  if (tabs.length > 0) {
    tabs[0].focus();
  }
}

function selectLastTab(tab: HTMLButtonElement) {
  const tabs = getTabs(tab);
  if (tabs.length > 0) {
    tabs[tabs.length - 1].focus();
  }
}

function getTabs(tab: HTMLButtonElement) {
  const tablist = tab.closest('[role="tablist"]');
  if (!tablist) {
    return [];
  }

  return [...tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]')];
}
